/**
 * Phase 5 D-09 — Zod schemas for the POST /api/ai/chat request body
 * and the SSE event envelope.
 *
 * ChatRequestSchema: validated at the /api/ai/chat route boundary.
 * SseEventSchema: discriminated union over the named events (D-09, pluss
 *   memory_changed fra #191); used by both the backend to emit events and the
 *   frontend to narrow received events safely.
 *
 * Single source of truth — both apps/api routes and apps/web useAssistantStream
 * import from here (MED-8 schema-drift guard).
 */
import { z } from 'zod';

/** Loose UUID regex — accepts both v4 and v7 (36-char canonical form). */
const UuidSchema = z.string().regex(/^[0-9a-f-]{36}$/i, 'invalid_uuid');

// ── POST /api/ai/chat request body ───────────────────────────────────────────

/**
 * Fase 6 — optional inline image for the vision agent. The base64 payload comes
 * from POST /api/ai/vision/upload, which runs the sharp pipeline (resize 1024 /
 * EXIF-strip / webp) so the bytes here are always small, compressed webp. The
 * cap (~6 MB of base64 ≈ ~4.5 MB raw) is a generous backstop above the upload
 * endpoint's own 8 MB pre-resize guard.
 */
export const ChatImageSchema = z.object({
  /** base64-encoded webp bytes (no data: URI prefix). */
  data: z.string().min(1).max(6_000_000),
  media_type: z.literal('image/webp'),
});
export type ChatImage = z.infer<typeof ChatImageSchema>;

export const ChatRequestSchema = z.object({
  /** If omitted, the backend creates a new conversation and emits conversation_created. */
  conversation_id: UuidSchema.optional(),
  /** The user's message. Min 1 char, max 4096 chars. */
  message: z.string().min(1).max(4096),
  /** Optional: which suggested prompt chip was tapped (for instrumentation). */
  suggested_prompt_id: z.string().optional(),
  /** Fase 6: optional photo (weekly-/homework-plan) for the vision agent. */
  image: ChatImageSchema.optional(),
  /** Fase 6: the child the parent picked at upload ("Hvem sin plan?"). The
   * backend resolves the display name from this id — the model pre-fills it as
   * the assignee on every draft. */
  assignee_id: UuidSchema.optional(),
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

// ── SSE event envelope — D-09 8 named events ─────────────────────────────────
// Discriminated on `event` so the client can narrow the type safely.

export const SseEventSchema = z.discriminatedUnion('event', [
  /**
   * Sent first if a new conversation was implicitly created by the /chat request.
   * The client should update its conversation list + route state.
   */
  z.object({
    event: z.literal('conversation_created'),
    data: z.object({ id: UuidSchema, title: z.string().nullable() }),
  }),

  /**
   * An assistant text chunk after sentence-boundary flush (D-03).
   * Append to the current assistant bubble text.
   */
  z.object({
    event: z.literal('delta'),
    data: z.object({ text: z.string() }),
  }),

  /**
   * A tool call has started. The client renders a tool-trace card row in pending state.
   * `args` is the raw tool input (passed through for the trace card).
   */
  z.object({
    event: z.literal('tool_call_start'),
    data: z.object({ id: z.string(), name: z.string(), args: z.unknown() }),
  }),

  /**
   * A tool call has completed. The client flips the trace row to done.
   * `truncated` mirrors AI-06 bounded output flag.
   */
  z.object({
    event: z.literal('tool_call_end'),
    data: z.object({
      id: z.string(),
      name: z.string(),
      duration_ms: z.number().int().nonnegative(),
      result_summary: z.string(),
      truncated: z.boolean(),
    }),
  }),

  /**
   * The allergy filter detected allergens and injected a correction turn (D-03).
   * The client shows AllergyFilterBanner with the hit list.
   *
   * #136 F2: `discard_chars` er hvor mange tegn av den stoppede runden som
   * allerede er sendt som `delta`. Klienten kutter så mange tegn fra slutten av
   * teksten, så neste rundes svar ikke limes på et halvt utkast. Tekst fra
   * tidligere, ferdige runder i samme tur står igjen.
   */
  z.object({
    event: z.literal('filter_correction'),
    data: z.object({
      hits: z.array(z.object({ allergen: z.string(), synonym: z.string() })),
      discard_chars: z.number().int().nonnegative(),
    }),
  }),

  /**
   * Diagnostic: signals which round of the 6-round agentic loop is starting (D-10).
   * The v1 client ignores this; kept for future debugging UI.
   */
  z.object({
    event: z.literal('round_advance'),
    data: z.object({ round: z.number().int().min(1).max(6), round_cap: z.literal(6) }),
  }),

  /**
   * The assistant turn has completed successfully. The client removes the streaming cursor.
   * `food_adjacent` tells the client whether to render the child advisory footer (AI-10).
   */
  z.object({
    event: z.literal('done'),
    data: z.object({
      finish_reason: z.string(),
      usage: z.object({ input_tokens: z.number().int(), output_tokens: z.number().int() }),
      cost_usd: z.number(),
      food_adjacent: z.boolean(),
    }),
  }),

  /**
   * AI-21 (#191): et notat i assistentens hukommelse ble lagret, venter på en
   * forelder, eller ble glemt. Klienten viser en brikke i chatten («Husket: …»
   * med Angre, «Foreslått for foreldrene», «Glemt: …»; #193). Sendes etter at
   * transaksjonen er committet (Pattern 2). Ingenting sender den før #192.
   *
   * #212: `updated` er `remember({ replaces_ref })` — et eksisterende notat fikk
   * ny tekst. Brikken er «Oppdatert: …» uten Angre: å slette notatet ville
   * fjernet mer enn endringen. Lagt til sist, så alt som var gyldig før, er
   * gyldig fortsatt.
   */
  z.object({
    event: z.literal('memory_changed'),
    data: z.object({
      action: z.enum(['saved', 'pending', 'forgotten', 'updated']),
      ref: z.number().int().positive(),
      id: UuidSchema,
      text: z.string(),
    }),
  }),

  /**
   * A recoverable or terminal error occurred. The client renders the matching
   * `assistant.error.*` i18n copy (D-13 error matrix).
   * `retry_after_seconds` is set for `rate_limit` and `api_rate_limit` codes.
   */
  z.object({
    event: z.literal('error'),
    data: z.object({
      code: z.enum([
        'rate_limit',
        'model_error',
        'network',
        'refusal',
        'aborted',
        'round_cap',
        'token_budget',
        'misconfigured',
        'food_block',
        // FF-13 (#181): AI av for familien, og familiens månedsbudsjett brukt opp.
        'ai_disabled',
        'ai_budget',
      ]),
      message_nb: z.string(),
      retry_after_seconds: z.number().int().nonnegative().optional(),
    }),
  }),
]);

export type SseEvent = z.infer<typeof SseEventSchema>;
